import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const initialState = {
  isLoading: false,
  isPreLoading: false,
  getError: null,
  gigsList: [],
  gigsDetail: [],
  pagination: {
    currentPage: 1,
    perPage: 12,
    total: 0,
    totalPages: 1
  },

  error: null ,
  singleGigDetail: [],
};

const allGigsSlice = createSlice({
  name: 'allGigs',
  initialState,
  reducers: {
    //START LOADING
    startLoading(state) {
      state.isLoading = true;
    },
    startPreLoading(state) {
      state.isPreLoading = true;
    },
    //STOP LOADING
    stopLoading(state) {
      state.isLoading = false;
      state.isPreLoading = false;
    },
    // HAS UPDATE ERROR
    hasGetError(state, action) {
      state.isLoading = false;
      state.isPreLoading = false;
      state.getError = action.payload;
    },
    // GET Gigs Success
    getGigssSuccess(state, action) {
      state.isLoading = false;
      state.isPreLoading = false;
      state.gigsList = action.payload;
    },
    // GET Gigs DETAILS
    getGigsDetailsSuccess(state, action) {
      state.isLoading = false;
      state.isPreLoading = false;
      state.gigsDetail = action.payload;
    },
    getSingleGigsDetailsSuccess(state, action) {
      state.isLoading = false;
      state.isPreLoading = false;
      state.singleGigDetail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Gigs
      .addCase(geAllGigs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(geAllGigs.fulfilled, (state, action) => {
  state.isLoading = false;
  state.gigsDetail = action.payload.data;   // flat gigs array
  state.pagination = action.payload.pagination; // pagination metadata
})


      .addCase(geAllGigs.rejected, (state, action) => {
        state.isLoading = false;
        state.getError = action.payload;
      })
      // Get Gig Detail
      .addCase(getGigDetail.pending, (state) => {
        state.isPreLoading = true;
      })
      .addCase(getGigDetail.fulfilled, (state, action) => {
        state.isPreLoading = false;
        state.singleGigDetail = action.payload;
      })
      .addCase(getGigDetail.rejected, (state, action) => {
        state.isPreLoading = false;
        state.getError = action.payload;
      });
  },
});

export const { 
  getGigsDetailsSuccess,
  startLoading,
  startPreLoading,
  stopLoading,
  hasGetError,
  getGigssSuccess,
  getSingleGigsDetailsSuccess 
} = allGigsSlice.actions;

export default allGigsSlice.reducer;

// In-memory cache for Stale-While-Revalidate
const gigsClientCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const geAllGigs = createAsyncThunk(
  'allGigs/getAllGigs',
  async ({ page = 1, limit = 4, categoryId = '', search = '', creatorRole = '', mode = '' }, { rejectWithValue }) => {
    try {
      const cacheKey = `gigs_v2_${page}_${limit}_${categoryId}_${search}_${creatorRole}_${mode}`;
      
      // If browsing without search query, check memory cache for 0ms render
      if (!search && gigsClientCache.has(cacheKey)) {
        const memCached = gigsClientCache.get(cacheKey);
        if (Date.now() - memCached.timestamp < CACHE_TTL) {
          return memCached.data;
        }
      }

      // Check sessionStorage cache
      if (!search) {
        try {
          const sessionItem = sessionStorage.getItem(`gt_v2_${cacheKey}`);
          if (sessionItem) {
            const parsed = JSON.parse(sessionItem);
            if (Date.now() - parsed.timestamp < CACHE_TTL) {
              gigsClientCache.set(cacheKey, parsed);
              return parsed.data;
            }
          }
        } catch (e) {}
      }

      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get('category-wise-gigs', {
        params: {
          page,
          per_page: limit,
          category_id: categoryId,
          search,
          creator_role: creatorRole,
          mode
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + accessToken
        }
      });
      
      // Strictly filter out any gigs that don't have valid images
      if (response?.data?.data && Array.isArray(response.data.data)) {
        response.data.data = response.data.data.filter(g => {
          const img = g?.media?.image1 || g?.media?.image2 || g?.media?.image3;
          return Boolean(img && typeof img === 'string' && img.trim() !== '' && img.trim() !== 'null' && img.trim() !== 'undefined');
        });
      }

      if (!search && response?.data) {
        const payload = { timestamp: Date.now(), data: response.data };
        gigsClientCache.set(cacheKey, payload);
        try {
          sessionStorage.setItem(`gt_v2_${cacheKey}`, JSON.stringify(payload));
        } catch (e) {}
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// Fast standalone single category gig fetcher with SWR cache
export const fetchCategoryGigsDirect = async ({ categoryId, creatorRole = 'bd', limit = 8 }) => {
  const cacheKey = `cat_gigs_v2_${categoryId}_${creatorRole}_${limit}`;
  if (gigsClientCache.has(cacheKey)) {
    const mem = gigsClientCache.get(cacheKey);
    if (Date.now() - mem.timestamp < CACHE_TTL) return mem.data;
  }
  try {
    const sessionItem = sessionStorage.getItem(`gt_v2_${cacheKey}`);
    if (sessionItem) {
      const parsed = JSON.parse(sessionItem);
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        gigsClientCache.set(cacheKey, parsed);
        return parsed.data;
      }
    }
  } catch (e) {}

  try {
    const accessToken = localStorage.getItem('accessToken');
    const response = await axios.get('category-wise-gigs', {
      params: {
        page: 1,
        per_page: limit,
        category_id: categoryId,
        creator_role: creatorRole
      },
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    });

    const rawGigs = response.data?.data || [];
    const gigs = rawGigs.filter(g => {
      const img = g?.media?.image1 || g?.media?.image2 || g?.media?.image3;
      return Boolean(img && typeof img === 'string' && img.trim() !== '' && img.trim() !== 'null' && img.trim() !== 'undefined');
    });
    const payload = { timestamp: Date.now(), data: gigs };
    gigsClientCache.set(cacheKey, payload);
    try {
      sessionStorage.setItem(`gt_v2_${cacheKey}`, JSON.stringify(payload));
    } catch (e) {}
    return gigs;
  } catch (err) {
    console.error(`Error loading gigs for category ${categoryId}:`, err);
    return [];
  }
};



export const getGigDetail = createAsyncThunk(
  'allGigs/getGigDetail',
  async (gigId, { rejectWithValue }) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get('gig-detail/' + gigId, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + accessToken
        }
      });
      
    
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);